import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * 並べ替えを保存するまでの待ち時間。
 * 保存ボタンを出さない代わりに、操作が終わってからこの時間で送る。
 * 1 手ごとに送ると自動配車 (lockType: 'sequence') が連続で走って計算が詰まる。
 */
export const SAVE_DEBOUNCE_MS = 2000;

/**
 * 最後の操作から SAVE_DEBOUNCE_MS 後に save を呼ぶ。
 * 続けて schedule すると待ち直し、最後の値だけを送る。
 */
export function useDebouncedSave<T>(save: (payload: T) => void) {
  const [isPending, setIsPending] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // save を依存に入れると、呼び出し側が毎回作り直したときに schedule の同一性が壊れる
  const saveRef = useRef(save);
  saveRef.current = save;

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const schedule = useCallback((payload: T) => {
    clear();
    setIsPending(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setIsPending(false);
      saveRef.current(payload);
    }, SAVE_DEBOUNCE_MS);
  }, [clear]);

  /** 取り消したあとに送ると、画面に無い並びが保存される */
  const cancel = useCallback(() => {
    clear();
    setIsPending(false);
  }, [clear]);

  // 画面を離れたあとに送ると、戻ってきたとき身に覚えのない並びになっている
  useEffect(() => clear, [clear]);

  return { schedule, cancel, isPending };
}
